import { Routes } from '@angular/router';
import { PageRoutes } from './core/constants/pages-routes';
import { MainLayoutComponent } from './core/layout/main-layout/main-layout.component';
import { AuthGuard } from './core/guards/auth.guard';
import { PermissionsGuard } from './core/guards/permission.guard';
import { UserAccess } from './core/constants/userAccess';

export const routes: Routes = [
  {
    path: PageRoutes.AUTH,
    loadChildren: () => import('./auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  {
    path: PageRoutes.PAGE,
    component: MainLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: PageRoutes.SETUP,
        loadChildren: () =>
          import('./pages/setup/setup.routes').then((m) => m.SETUP_ROUTES),
        canActivateChild: [PermissionsGuard],
        data: {
          permissions: {
            only: [
              UserAccess.superAdmin,
              UserAccess.exporter,
              UserAccess.viewUserSetup,
              UserAccess.viewOrganizationSetup,
              UserAccess.viewQuotaSetup,
              UserAccess.auditorQCVS,
              UserAccess.auditorPH
            ],
            redirectTo: PageRoutes.PAGE + '/' + PageRoutes.PAGE_NOT_FOUND,
          },
        },
      },
      {
        path: PageRoutes.SETTINGS,
        loadChildren: () =>
          import('./pages/settings/settings.routes').then(
            (m) => m.SETTINGS_ROUTES,
          ),
        canActivate: [PermissionsGuard],
        title: 'NZMB-Settings',
        data: {
          permissions: {
            only: [UserAccess.superAdmin, UserAccess.allowSystemSettings],
            redirectTo: PageRoutes.PAGE + '/' + PageRoutes.PAGE_NOT_FOUND,
          },
        },
      },
      {
        path: PageRoutes.DASHBOARD,
        loadChildren: () =>
          import('./pages/dashboard/dashboard.routes').then(
            (m) => m.DASHBOARD_ROUTES,
          ),
        canActivate: [PermissionsGuard],
        title: 'NZMB-Dashboard',
        data: {
          permissions: {
            only: [
              UserAccess.superAdmin,
              UserAccess.nzmbStaff,
              UserAccess.exporter,
              UserAccess.exporterManager
            ],
            redirectTo: PageRoutes.PAGE + '/' + PageRoutes.PAGE_NOT_FOUND,
          },
        },
      },
      {
        path: PageRoutes.CERTIFICATE_MANAGER,
        loadChildren: () =>
          import('./pages/certificate-manager/certificate-manager.routes').then(
            (m) => m.CERTIFICATE_MANAGER_ROUTES,
          ),
        canActivate: [PermissionsGuard],
        title: 'NZMB-Certificate Manager',
        data: {
          permissions: {
            only: [
              UserAccess.superAdmin,
              UserAccess.allowCertificateRequest
            ],
            redirectTo: PageRoutes.PAGE + '/' + PageRoutes.PAGE_NOT_FOUND,
          },
        },
      },
      {
        path: PageRoutes.PRODUCTION_HISTORY,
        loadChildren: () =>
          import('./pages/production-history/production-history.routes').then(
            (m) => m.PRODUCTION_HISTORY_ROUTES,
          ),
      },
      {
        path: PageRoutes.QUOTA_ALLOCATOR,
        loadChildren: () =>
          import('./pages/quota-allocator/quota-allocator.routes').then(
            (m) => m.QUOTA_ALLOCATOR_ROUTES,
          ),
      },
      {
        path: PageRoutes.QUOTA_MANAGER,
        loadChildren: () =>
          import('./pages/quota-manager/quota-manager.routes').then(
            (m) => m.QUOTA_MANAGER_ROUTES,
          ),
        canActivate: [PermissionsGuard],
        data: {
          permissions: {
            only: [UserAccess.superAdmin, UserAccess.nzmbStaff, UserAccess.exporter],
            redirectTo: PageRoutes.PAGE + '/' + PageRoutes.PAGE_NOT_FOUND,
          },
        },
        title: 'NZMB-Quota Manager',
      },
      {
        path: PageRoutes.EXPORT_HISTORY,
        loadChildren: () =>
          import('./pages/export-history/export-history.routes').then(
            (m) => m.EXPORT_HISTORY_ROUTES,
          ),
        canActivate: [PermissionsGuard],
        title: 'NZMB-Export History',
        data: {
          permissions: {
            only: [UserAccess.superAdmin, UserAccess.viewExporterHistory],
            redirectTo: PageRoutes.PAGE + '/' + PageRoutes.PAGE_NOT_FOUND,
          },
        },
      },
      // {
      //   path: PageRoutes.TRANSACTION,
      //   loadComponent: () =>
      //     import('./pages/transaction/transaction.component').then(
      //       (m) => m.TransactionComponent
      //     ),
      // },
      {
        path: PageRoutes.AUDITS,
        loadChildren: () =>
          import('./pages/audits/audits.routes').then((m) => m.AUDITS_ROUTES),
      },
      {
        path: PageRoutes.COMMUNICATION,
        loadChildren: () =>
          import('./pages/communication/communication.routes').then(
            (m) => m.COMMUNICATION_ROUTES,
          ),
        canActivate: [PermissionsGuard],
        title: 'NZMB-Communication',
        data: {
          permissions: {
            only: [UserAccess.superAdmin, UserAccess.viewCommunication],
            redirectTo: PageRoutes.PAGE + '/' + PageRoutes.PAGE_NOT_FOUND,
          },
        },
      },
      {
        path: PageRoutes.REPORTS,
        loadChildren: () =>
          import('./pages/reports/reports.routes').then(
            (m) => m.REPORTS_ROUTES,
          ),
        canActivate: [PermissionsGuard],
        title: 'NZMB-Reports',
        data: {
          permissions: {
            only: [UserAccess.superAdmin, UserAccess.viewReports],
            redirectTo: PageRoutes.PAGE + '/' + PageRoutes.PAGE_NOT_FOUND,
          },
        },
      },
      {
        path: PageRoutes.PAGE_NOT_FOUND,
        loadComponent: () =>
          import('./pages/page-not-found/page-not-found.component').then(
            (m) => m.PageNotFoundComponent,
          ),
      },
    ],
  },
  {
    path: `${PageRoutes.RENEW_ORGANIZATION}/:userId/:token`,
    loadComponent: () =>
      import('./auth/renew-organization/renew-organization.component').then(
        (m) => m.RenewOrganizationComponent,
      ),
  },
  {
    path: PageRoutes.EXPORTER_SIGNUP,
    loadComponent: () =>
      import('./auth/exporter-signup/exporter-signup.component').then(
        (m) => m.ExporterSignupComponent,
      ),
  },
  {
    path: PageRoutes.SELECT_ORGANIZATION,
    loadComponent: () =>
      import('./auth/select-organization/select-organization.component').then(
        (m) => m.SelectOrganizationComponent,
      ),
  },
  {
    path: PageRoutes.CALLBACK,
    loadComponent: () =>
      import('./auth/callback/callback.component').then(
        (m) => m.CallbackComponent,
      ),
  },
  {
    path: PageRoutes.UNAUTHORIZED,
    loadComponent: () =>
      import('./auth/unauthorized/unauthorized.component').then(
        (m) => m.UnauthorizedComponent,
      ),
  },
  {
    path: '',
    loadComponent: () =>
      import('./auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: '**',
    loadComponent: () =>
      import('./auth/login/login.component').then((m) => m.LoginComponent),
  },
];
